import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TechUserFilter from '../components/TechUserFilter';
import {
  FiClock,
  FiLogIn,
  FiLogOut,
  FiPlay,
  FiSquare,
  FiLoader,
  FiUsers,
  FiCalendar,
  FiPlus,
} from 'react-icons/fi';
import {
  STATUS_COLORS,
  formatDateTimeShort,
  formatDurationMinutes,
  liveEntryMinutes,
} from '../utils/techTask';

function istDateInput(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(date);
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return istDateInput(d);
}

const TechTimesheet = () => {
  const { isTechAdmin } = useAuth();
  const [today, setToday] = useState(null);
  const [team, setTeam] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const [historyFrom, setHistoryFrom] = useState(daysAgo(13));
  const [historyTo, setHistoryTo] = useState(istDateInput());
  const [historyUser, setHistoryUser] = useState('all');
  const [showQuickTask, setShowQuickTask] = useState(false);
  const [quickTitle, setQuickTitle] = useState('');
  const [quickClient, setQuickClient] = useState('');

  const loadToday = useCallback(async () => {
    const res = await api.get('/tech-timesheet/today');
    setToday(res.data);
  }, []);

  const loadTeam = useCallback(async () => {
    if (!isTechAdmin) return;
    const res = await api.get('/tech-timesheet/team/today');
    setTeam(res.data.team || []);
  }, [isTechAdmin]);

  const loadHistory = useCallback(async () => {
    const userParam = isTechAdmin ? historyUser : 'me';
    const res = await api.get('/tech-timesheet/history', {
      params: { from: historyFrom, to: historyTo, user_id: userParam },
    });
    setHistory(res.data.days || []);
  }, [historyFrom, historyTo, historyUser, isTechAdmin]);

  const refresh = useCallback(async () => {
    try {
      await Promise.all([loadToday(), loadTeam(), loadHistory()]);
    } catch (e) {
      console.error(e);
    }
  }, [loadToday, loadTeam, loadHistory]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await refresh();
      setLoading(false);
    };
    init();
  }, [refresh]);

  useEffect(() => {
    if (!today?.running_entry) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [today?.running_entry]);

  const runAction = async (fn, successMsg) => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      await fn();
      setMessage(successMsg);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckIn = () =>
    runAction(() => api.post('/tech-timesheet/check-in'), 'Checked in — pick a task to start your timer.');

  const handleCheckOut = () =>
    runAction(() => api.post('/tech-timesheet/check-out'), 'Checked out for today. See you tomorrow!');

  const handleStartTask = (taskId) =>
    runAction(() => api.post(`/tech-timesheet/tasks/${taskId}/start`), 'Timer started on task.');

  const handleStopTimer = () =>
    runAction(() => api.post('/tech-timesheet/tasks/stop'), 'Timer stopped.');

  const handleQuickTask = async (e) => {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    await runAction(
      () => api.post('/tech-timesheet/quick-task', {
        title: quickTitle.trim(),
        client_name: quickClient.trim() || undefined,
      }),
      'Ticket created in Tickets and timer started.'
    );
    setQuickTitle('');
    setQuickClient('');
    setShowQuickTask(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-400" />
      </div>
    );
  }

  const checkedIn = today?.checked_in;
  const checkedOut = today?.checked_out;
  const running = today?.running_entry;
  const runningMinutes = running ? liveEntryMinutes(running.started_at) : 0;
  void tick; // re-render every second while timer runs

  return (
    <div className="space-y-6 max-w-4xl pb-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Timesheet</h1>
        <p className="text-slate-400 text-sm mt-1">
          Check in daily, track time on tickets, check out when you leave
        </p>
      </div>

      {message && <p className="text-sm text-teal-300 bg-teal-500/10 border border-teal-500/20 rounded-xl px-4 py-3">{message}</p>}
      {error && <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">{error}</p>}

      <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Today</p>
            <p className="text-lg font-semibold text-white">{today?.work_date}</p>
            {today?.attendance?.check_in_at && (
              <p className="text-xs text-slate-400 mt-1">
                In {formatDateTimeShort(today.attendance.check_in_at)}
                {today.attendance.check_out_at && (
                  <> · Out {formatDateTimeShort(today.attendance.check_out_at)}</>
                )}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Logged today</p>
            <p className="text-2xl font-bold text-teal-300">{today?.total_label || '0 min'}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {!checkedIn && !checkedOut && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={handleCheckIn}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-teal-500 text-slate-950 font-semibold text-sm disabled:opacity-50 touch-manipulation"
            >
              <FiLogIn size={18} />
              Check in
            </button>
          )}
          {checkedIn && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={handleCheckOut}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-500 text-slate-950 font-semibold text-sm disabled:opacity-50 touch-manipulation"
            >
              <FiLogOut size={18} />
              Check out
            </button>
          )}
          {checkedOut && (
            <span className="flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-800 text-slate-400 text-sm">
              <FiClock size={16} />
              Day complete
            </span>
          )}
        </div>

        {running && (
          <div className="rounded-xl bg-teal-500/10 border border-teal-500/30 p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-teal-300/80 uppercase tracking-wide flex items-center gap-1">
                <FiLoader className="animate-spin" size={12} />
                Timer running
              </p>
              <p className="font-semibold text-white mt-1">{running.task_title}</p>
              <p className="text-sm text-teal-300 mt-0.5">
                {formatDurationMinutes(runningMinutes)} · since {formatDateTimeShort(running.started_at)}
              </p>
            </div>
            <button
              type="button"
              disabled={actionLoading}
              onClick={handleStopTimer}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-slate-200 text-sm hover:bg-slate-700 disabled:opacity-50"
            >
              <FiSquare size={14} />
              Stop
            </button>
          </div>
        )}
      </div>

      {checkedIn && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="font-semibold text-white">Your active tickets</h2>
            {!showQuickTask && (
              <button
                type="button"
                onClick={() => setShowQuickTask(true)}
                className="flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 touch-manipulation"
              >
                <FiPlus size={16} />
                Custom task
              </button>
            )}
          </div>

          {showQuickTask && (
            <form
              onSubmit={handleQuickTask}
              className="rounded-xl border border-teal-500/30 bg-teal-500/5 p-4 mb-3 space-y-3"
            >
              <p className="text-sm text-teal-200/90">
                Adds a ticket under your name and starts the timer immediately.
              </p>
              <input
                required
                value={quickTitle}
                onChange={(e) => setQuickTitle(e.target.value)}
                placeholder="What are you working on?"
                className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              />
              <input
                value={quickClient}
                onChange={(e) => setQuickClient(e.target.value)}
                placeholder="Client / project (optional)"
                className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickTask(false);
                    setQuickTitle('');
                    setQuickClient('');
                  }}
                  className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-300 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || !quickTitle.trim()}
                  className="flex-1 py-2.5 rounded-xl bg-teal-500 text-slate-950 font-semibold text-sm disabled:opacity-50"
                >
                  Add & start timer
                </button>
              </div>
            </form>
          )}

          {today?.tasks?.length === 0 && !showQuickTask ? (
            <div className="text-center rounded-xl border border-slate-800 bg-slate-900/50 py-8 px-4">
              <p className="text-slate-500 text-sm">No pending or in-progress tickets assigned to you.</p>
              <button
                type="button"
                onClick={() => setShowQuickTask(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-500/20 text-teal-300 text-sm font-medium hover:bg-teal-500/30 touch-manipulation"
              >
                <FiPlus size={16} />
                Add custom task
              </button>
            </div>
          ) : today?.tasks?.length > 0 ? (
            <div className="space-y-2">
              {today.tasks.map((t) => {
                const isRunning = running?.technical_task_id === t.id;
                return (
                  <div
                    key={t.id}
                    className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${
                      isRunning
                        ? 'border-teal-500/40 bg-teal-500/10'
                        : 'border-slate-800 bg-slate-900'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <Link to={`/tech/tasks/${t.id}`} className="font-medium text-white hover:text-teal-300">
                        {t.title}
                      </Link>
                      {t.client_name && (
                        <p className="text-xs text-slate-500 mt-0.5">{t.client_name}</p>
                      )}
                    </div>
                    <span className={`${STATUS_COLORS[t.status] || 'bg-slate-600'} text-white text-[10px] px-2 py-0.5 rounded-full capitalize`}>
                      {t.status.replace('_', ' ')}
                    </span>
                    {!isRunning && (
                      <button
                        type="button"
                        disabled={actionLoading}
                        onClick={() => handleStartTask(t.id)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-500/20 text-teal-300 text-xs font-medium hover:bg-teal-500/30 disabled:opacity-50 touch-manipulation"
                      >
                        <FiPlay size={12} />
                        Start timer
                      </button>
                    )}
                    {isRunning && (
                      <span className="text-xs text-teal-300 font-medium">Tracking…</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      )}

      {today?.entries?.length > 0 && (
        <div>
          <h2 className="font-semibold text-white mb-3">Today&apos;s log</h2>
          <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden divide-y divide-slate-800">
            {today.entries.map((e) => (
              <div key={e.id} className="px-4 py-3 flex justify-between gap-3 text-sm">
                <div>
                  <p className="text-white">{e.task_title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formatDateTimeShort(e.started_at)}
                    {e.ended_at ? ` → ${formatDateTimeShort(e.ended_at)}` : ' → running'}
                  </p>
                </div>
                <span className="text-teal-400 shrink-0">{e.duration_label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isTechAdmin && (
        <div>
          <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
            <FiUsers size={18} />
            Team today
          </h2>
          <div className="space-y-2">
            {team.map((m) => (
              <div key={m.user_id} className="rounded-xl bg-slate-900 border border-slate-800 px-4 py-3 flex flex-wrap justify-between gap-2">
                <div>
                  <p className="text-white text-sm font-medium">{m.name}</p>
                  {m.running_entry ? (
                    <p className="text-xs text-teal-400 mt-0.5">
                      {m.running_entry.task_title} · {m.running_entry.duration_label}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-0.5">No active timer</p>
                  )}
                </div>
                <span className={`text-xs shrink-0 ${
                  m.checked_in ? 'text-emerald-400' : m.checked_out ? 'text-slate-500' : 'text-amber-400'
                }`}>
                  {m.checked_in ? 'Checked in' : m.checked_out ? 'Checked out' : 'Not checked in'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
          <FiCalendar size={18} />
          Timesheet history
        </h2>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            type="date"
            value={historyFrom}
            onChange={(e) => setHistoryFrom(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 text-white text-sm"
          />
          <input
            type="date"
            value={historyTo}
            onChange={(e) => setHistoryTo(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 text-white text-sm"
          />
          {isTechAdmin && (
            <TechUserFilter
              value={historyUser}
              onChange={setHistoryUser}
              allLabel="Everyone"
              className="sm:min-w-[180px]"
            />
          )}
          <button
            type="button"
            onClick={loadHistory}
            className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm hover:bg-slate-700"
          >
            Apply
          </button>
        </div>

        {history.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">No timesheet records in this range.</p>
        ) : (
          <div className="space-y-3">
            {history.map((day) => (
              <div key={`${day.user_id}-${day.work_date}`} className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-800 flex flex-wrap justify-between gap-2">
                  <div>
                    <p className="text-white font-medium">
                      {day.work_date}
                      {isTechAdmin && day.user_name && (
                        <span className="text-slate-400 font-normal"> · {day.user_name}</span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {day.check_in_at && formatDateTimeShort(day.check_in_at)}
                      {day.check_out_at && ` → ${formatDateTimeShort(day.check_out_at)}`}
                    </p>
                  </div>
                  <span className="text-teal-400 text-sm font-medium">{day.total_label}</span>
                </div>
                {day.entries?.length > 0 && (
                  <div className="divide-y divide-slate-800">
                    {day.entries.map((e) => (
                      <div key={e.id} className="px-4 py-2 flex justify-between gap-2 text-sm">
                        <span className="text-slate-300">{e.task_title}</span>
                        <span className="text-slate-500 shrink-0">{e.duration_label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TechTimesheet;
